import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { timezoneConfig } from 'src/app/common/constants/timezone';
import { VisitorEvent } from '../../shared/models/visitor.event';
import { frontendSettingsSelector } from '../../shared/ngrx.stores/frontend.settings/selectors';
import { IAppState, IFrontendSettings } from '../../shared/ngrx.stores/frontend.settings/states';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import * as advancedFormat from 'dayjs/plugin/advancedFormat';
import * as duration from 'dayjs/plugin/duration';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { timeConfig } from 'src/app/config/timeConfig';
import { displayableEventTimes, isUpcomingEventTime } from '../utils/event.time.display';

@Injectable({
  providedIn: 'root',
})
export class EventTimeService {
  frontendSettings$: Observable<IFrontendSettings | null>;
  defaultTimezoneFormatter: string = null;
  monthText = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  constructor(private _store: Store<IAppState>) {
    this.frontendSettings$ = this._store.pipe(select(frontendSettingsSelector));
    this.frontendSettings$.subscribe((settings) => {
      if (!settings) return;

      this.defaultTimezoneFormatter = settings.customSettings.timezoneFormatter;
    });

    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(advancedFormat);
    dayjs.extend(duration);
  }

  public processEventTimes(event: VisitorEvent) {
    this.setTimezoneFormatter(event);
    this.sortEventTimes(event);

    if (!event.eventTimes || event.eventTimes.length === 0) {
      event.isExpired = true;
      return null;
    }

    const decorated = event.eventTimes.map((time) => {
      const start = dayjs(time.startTime).tz(event.timezoneFormatter);
      const normalizedIsAllDay = time.isAllDay === true
        || (time.isAllDay !== false && start.hour() === 0 && start.minute() === 0);
      const end = time.endTime ? dayjs(time.endTime).tz(event.timezoneFormatter) : null;
      const normalizedIsEndAllDay = (!!time.endTime && time.isEndAllDay === true)
        || (time.isEndAllDay !== false && normalizedIsAllDay
            && !!end && end.hour() === 0 && end.minute() === 0);
      return {
        startTime: time.startTime,
        endTime: time.endTime,
        isAllDay: normalizedIsAllDay,
        isEndAllDay: normalizedIsEndAllDay,
        timeTwoLines: this.getTimeTwoLines({ ...time, isAllDay: normalizedIsAllDay, isEndAllDay: normalizedIsEndAllDay }, event.timezoneFormatter),
        timeOneLine: this.getTimeOneLine({ ...time, isAllDay: normalizedIsAllDay, isEndAllDay: normalizedIsEndAllDay }, event.timezoneFormatter),
        month: start.format('MMM'),
        day: start.format('D'),
        year: start.format('YYYY'),
        startTimeFormatted: start.tz(event.timezoneFormatter).format('MMM D [@] h:mm z'),
      };
    });

    event.eventTimes = decorated;
    event.startTime = decorated[0].startTime;
    event.endTime = decorated[decorated.length - 1].endTime;
    event.startTimeFormatted = decorated[0].startTimeFormatted;

    const currentTime = this.getCurrentTimeInTimezone(event.timezoneFormatter);
    const stillUpcoming = decorated.some((t) => isUpcomingEventTime(t,
      this.resolveTimezoneFormatter(event.timezoneFormatter), currentTime));
    event.isExpired = !stillUpcoming;

    const firstUpcoming =
      decorated.find((t) => isUpcomingEventTime(t,
        this.resolveTimezoneFormatter(event.timezoneFormatter), currentTime)) ?? decorated[0];
    const isAllDay = firstUpcoming.isAllDay;
    const isEndAllDay = firstUpcoming.isEndAllDay;
    event.timeTwoLines = this.getTimeTwoLines(
      {
        startTime: firstUpcoming.startTime,
        endTime: firstUpcoming.endTime,
        isAllDay,
        isEndAllDay,
      },
      event.timezoneFormatter,
    );
    event.timeOneLine = this.getTimeOneLine(
      {
        startTime: firstUpcoming.startTime,
        endTime: firstUpcoming.endTime,
        isAllDay,
        isEndAllDay,
      },
      event.timezoneFormatter,
    );
  }

  /**
   * Returns a display-only filtered subset of upcoming occurrences without mutating the
   * source. Use this in list views that should hide past dates, instead of relying on
   * processEventTimes to silently strip them.
   */
  public getDisplayableEventTimes(event: VisitorEvent) {
    if (!event?.eventTimes) return [];
    const currentTime = this.getCurrentTimeInTimezone(event?.timezoneFormatter);
    return displayableEventTimes(event.eventTimes,
      this.resolveTimezoneFormatter(event.timezoneFormatter), currentTime);
  }

  public formatToShareTime(time: string): string {
    return dayjs(time)
      .tz(this.defaultTimezoneFormatter)
      .format(ConfigService.config.eventShare.mailDateFormat);
  }

  public toDateFilterTime(time: string | Date, timezoneFormatter?: string) {
    const formatted = timezoneFormatter
      ? dayjs(time).tz(this.resolveTimezoneFormatter(timezoneFormatter))
      : dayjs(time);
    return formatted.format('MM/DD/YYYY');
  }
  public toDate(time: string | Date, timezoneFormatter: string) {
    return dayjs(time).tz(timezoneFormatter).format('YYYY-MM-DD');
  }

  public toISOTime(time: string | Date, timezoneFormatter: string) {
    return dayjs.tz(time, timezoneFormatter).toISOString();
  }

  public getTZNameByFormatter(formatter: string) {
    return timezoneConfig.find((x) => x.value === formatter)?.name || formatter;
  }

  public getTZByFormatter(formatter: string) {
    return timezoneConfig.find((x) => x.value === formatter)?.value || formatter || '';
  }

  public toICSDate(time: string | Date, formatter: string) {
    return dayjs(time).tz(formatter).format('YYYY-MM-DD');
  }

  public toICSTime(time: string | Date, formatter: string) {
    return dayjs(time).tz(formatter).format('HH:mm');
  }

  public isAllDayEvent(time: string | Date, formatter: string, isAllDay?: boolean): boolean {
    if (isAllDay === true) return true;
    const start = dayjs(time).tz(formatter);
    return start.hour() === 0;
  }

  public getLocalHourByTZF(time: Date | string, formatter: string) {
    const hour = dayjs(time).tz(formatter).format('HH');
    return parseInt(hour, 10);
  }
  public getLocalTimeByTZF(time: Date | string, formatter: string) {
    const localTime = dayjs(time).tz(formatter).format('HH:mm');
    return localTime.startsWith('0') ? localTime.slice(1) : localTime;
  }

  public toShortDateFormat(x: Date | string) {
    return dayjs(x).format('MMM.DD ddd');
  }
  public toDateFormat(x: Date | string, timezoneFormatter?: string) {
    const formatted = timezoneFormatter
      ? dayjs(x).tz(this.resolveTimezoneFormatter(timezoneFormatter))
      : dayjs(x);
    return formatted.format('YYYY-MM-DD');
  }
  public toPDFTimeFormat(original: Date): string {
    return dayjs(original).format('MMM D Y');
  }

  public getDiff(timeA: Date | string, timeB: Date | string) {
    let a = dayjs(timeA);
    let b = dayjs(timeB);
    return a.diff(b, 'day');
  }

  public convertToTravelTimeText(totalTravelTime: number) {
    // Match the rounded minutes stored for itineraries and displayed by SSR.
    const totalMinutes = Math.round(totalTravelTime / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let timeString = '';
    if (hours > 0) {
      timeString += hours + ' hrs';
      if (minutes > 0) {
        timeString += ', ';
      }
    }
    if (minutes > 0) {
      timeString += minutes + ' min';
    }
    return timeString;
  }

  public addToDate(date: Date | string, unit: dayjs.ManipulateType, amount: number) {
    const currentDate = dayjs(date);
    return currentDate.add(amount, unit);
  }

  public getOnlyDisplayDate(target: Date | string) {
    const date = new Date(target);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    return `${this.monthText[month]}.${day}, ${year}`;
  }

  /**
   * Check if a place is open now based on its opening hours and timezone.
   * @param {Array} periods - Array of opening and closing periods.
   * @param {string} timezone - Timezone (e.g., "America/New_York").
   * @returns {boolean} - True if the place is open, false otherwise.
   */
  public isPlaceOpenNow(
    periods: {
      open: { day: number; time: string };
      close: { day: number; time: string };
    }[],
    timezone: string
  ): boolean {
    if (!periods || periods.length === 0) {
      return false;
    }

    // Check for 24/7 operation: single period with day=0, time='0000', and no close
    if (
      periods.length === 1 &&
      periods[0].open.day === 0 &&
      periods[0].open.time === '0000' &&
      !periods[0].close
    ) {
      return true;
    }

    const now = dayjs().tz(timezone);
    const currentDay = now.day(); // 0 = Sunday, 6 = Saturday
    const currentTime = now.format('HHmm'); // Current time as HHMM string

    for (const period of periods) {
      const { open, close } = period;
      
      // Skip periods without close time (invalid data)
      if (!close || !close.time) {
        continue;
      }

      const isCurrentlyInPeriod = this.checkIfTimeInPeriod(
        currentDay,
        currentTime,
        open.day,
        open.time,
        close.day,
        close.time
      );

      if (isCurrentlyInPeriod) {
        return true;
      }
    }

    return false;
  }

  public checkIfTimeInPeriod(
    currentDay: number,
    currentTime: string,
    openDay: number,
    openTime: string,
    closeDay: number,
    closeTime: string
  ): boolean {
    // Case 1: Same day operation (open and close on same day)
    if (openDay === closeDay) {
      if (currentDay === openDay) {
        // Normal hours (e.g., 09:00 - 17:00)
        if (openTime <= closeTime) {
          return currentTime >= openTime && currentTime < closeTime;
        }
        // Overnight on same day (e.g., 22:00 - 06:00 next day)
        else {
          return currentTime >= openTime || currentTime < closeTime;
        }
      }
      // Check if we're in the "next day" part of an overnight period
      else if (openTime > closeTime) {
        const previousDay = (currentDay - 1 + 7) % 7;
        if (previousDay === openDay) {
          return currentTime < closeTime;
        }
      }
      return false;
    }

    // Case 2: Multi-day operation (open on one day, close on another)
    else {
      const daysDiff = (closeDay - openDay + 7) % 7;

      if (daysDiff === 1) {
        // Open one day, close next day
        if (currentDay === openDay) {
          return currentTime >= openTime;
        } else if (currentDay === closeDay) {
          return currentTime < closeTime;
        }
      } else {
        // Multi-day operation (rare, but possible)
        const currentDayOffset = (currentDay - openDay + 7) % 7;

        if (currentDayOffset === 0) {
          // Opening day
          return currentTime >= openTime;
        } else if (currentDayOffset === daysDiff) {
          // Closing day
          return currentTime < closeTime;
        } else if (currentDayOffset > 0 && currentDayOffset < daysDiff) {
          // Middle days (fully open)
          return true;
        }
      }

      return false;
    }
  }

  // Alternative simpler approach using dayjs for date calculations
  public isPlaceOpenNowAlternative(
    periods: {
      open: { day: number; time: string };
      close: { day: number; time: string };
    }[],
    timezone: string
  ): boolean {
    const now = dayjs().tz(timezone);

    for (const { open, close } of periods) {
      // Create opening time
      const openHour = parseInt(open.time.slice(0, 2));
      const openMinute = parseInt(open.time.slice(2));

      // Create closing time
      const closeHour = parseInt(close.time.slice(0, 2));
      const closeMinute = parseInt(close.time.slice(2));

      // Find the most recent opening time
      let openTime = now.clone().day(open.day).hour(openHour).minute(openMinute).second(0);

      // If opening time is in the future, try previous week
      if (openTime.isAfter(now)) {
        openTime = openTime.subtract(1, 'week');
      }

      // Calculate closing time based on opening time
      let closeTime = openTime.clone().day(close.day).hour(closeHour).minute(closeMinute).second(0);

      // If close day is before open day or same day but earlier time, it's next occurrence
      if (close.day < open.day || (close.day === open.day && close.time <= open.time)) {
        closeTime = closeTime.add(1, 'week');
      }

      // Check if current time is within the period
      if ((now.isAfter(openTime) || now.isSame(openTime)) && now.isBefore(closeTime)) {
        return true;
      }
    }

    return false;
  }

  // DEAD: only consumed by the @deprecated frequency selectors. RRuleGenerator handles
  // monthly Nth-weekday natively via rrule's `byweekday + bysetpos` options.
  /** @deprecated Use OccurrenceService.expand on a kind='monthly' RecurrenceRule. */
  public getSameDayInWeekOfMonth(
    start: string,
    dayOfWeek: number,
    no: number,
    timezone: string
  ): string {
    timezone = timezone || this.getTimezoneCode();
    const timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    const startInTZ = dayjs(start).tz(timezoneFormatter);
    const startLocalHour = startInTZ.hour();
    const startLocalMinute = startInTZ.minute();

    let current = dayjs(start).tz(timezoneFormatter).startOf('month');

    let meetNo = 0;
    while (true) {
      const currentDayOfWeek = current.tz(timezoneFormatter).day();
      if (currentDayOfWeek === dayOfWeek) {
        meetNo++;
        if (meetNo === no) {
          break;
        }
        current = current.add(7, 'day');
      } else {
        current = current.add(1, 'day');
      }
    }

    const dateStr = current.tz(timezoneFormatter).format('YYYY-MM-DD');
    return dayjs.tz(
      `${dateStr} ${String(startLocalHour).padStart(2, '0')}:${String(startLocalMinute).padStart(2, '0')}`,
      'YYYY-MM-DD HH:mm',
      timezoneFormatter
    ).toISOString();

  }

  // DEAD: only consumed by the @deprecated frequency selectors. Same replacement as above.
  /** @deprecated Use OccurrenceService.expand on a kind='monthly' RecurrenceRule. */
  public getSameDayInWeek(
    start: string,
    end: string,
    dayOfWeek: number,
    no: number,
    timezone: string
  ) {
    timezone = timezone || this.getTimezoneCode();
    const timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);

    let current = dayjs(start).tz(timezoneFormatter);
    const endTime = dayjs(end).tz(timezoneFormatter);
    // Preserve the original time-of-day so startOf('month') doesn't reset it to midnight
    const originalStart = dayjs(start).tz(timezoneFormatter);
    const days: string[] = [];

    while (current.isBefore(endTime)) {
      // Restore original time-of-day (lost by startOf('month') on previous iteration)
      const currentWithTime = current
        .hour(originalStart.hour())
        .minute(originalStart.minute())
        .second(originalStart.second());
      const targetDate = this.getSameDayInWeekOfMonth(
        currentWithTime.toISOString(),
        dayOfWeek,
        no,
        timezone
      );
      const targetDay = dayjs(targetDate).tz(timezoneFormatter);

      if (targetDay.isBefore(endTime)) {
        days.push(targetDay.toISOString());
        current = targetDay.add(1, 'month').startOf('month');
      } else {
        break;
      }
    }

    return days;
  }

  // DEAD: only consumed by the @deprecated frequency selectors. RRuleGenerator handles
  // last-weekday-of-month via rrule's `bysetpos: [-1]`.
  /** @deprecated Use OccurrenceService.expand on a kind='last' RecurrenceRule. */
  public getLastWeekDays(
    start: string,
    end: string,
    dayOfWeek: number,
    timezone: string
  ): string[] {
    timezone = timezone || this.getTimezoneCode();
    const timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    const startInTZ = dayjs(start).tz(timezoneFormatter);
    const startLocalHour = startInTZ.hour();
    const startLocalMinute = startInTZ.minute();

    let current = dayjs(start).tz(timezoneFormatter);
    const endTime = dayjs(end).tz(timezoneFormatter);
    const days: string[] = [];

    while (current.isBefore(endTime)) {
      let lastDayOfMonth = current.add(1, 'month').startOf('month').subtract(1, 'day');

      while (lastDayOfMonth.tz(timezoneFormatter).day() !== dayOfWeek) {
        lastDayOfMonth = lastDayOfMonth.subtract(1, 'day');
      }
      const dateStr = lastDayOfMonth.tz(timezoneFormatter).format('YYYY-MM-DD');
      lastDayOfMonth = dayjs.tz(
        `${dateStr} ${String(startLocalHour).padStart(2, '0')}:${String(startLocalMinute).padStart(2, '0')}`,
        'YYYY-MM-DD HH:mm',
        timezoneFormatter
      );

      if (lastDayOfMonth.isBefore(endTime)) {
        // Restore the original time-of-day from the start time
        const originalTime = dayjs(start).tz(timezoneFormatter);
        const withTime = lastDayOfMonth.tz(timezoneFormatter)
          .hour(originalTime.hour())
          .minute(originalTime.minute())
          .second(originalTime.second());
        days.push(withTime.toISOString());
      } else {
        break;
      }
      current = current.add(1, 'month').startOf('month');
    }
    return days;
  }

  public getWeekDayNo(
    date: string | Date,
    timezone?: string
  ): {
    no: number;
    isLast: boolean;
    text: string;
  } {
    timezone = timezone || dayjs.tz.guess();
    let timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    const current = dayjs(date).tz(timezoneFormatter);
    const weekText = ['first', 'second', 'third', 'fourth', 'last'];

    let count = 0;
    let previousWeek = current;

    while (previousWeek.month() === current.month()) {
      previousWeek = previousWeek.subtract(1, 'week');
      count++;
    }

    const nextWeek = current.add(1, 'week');
    const isLast = nextWeek.month() !== current.month();

    return {
      no: count,
      isLast,
      text: isLast ? 'last' : weekText[count - 1] || '',
    };
  }

  public getDayAndMonth(date: string | Date, timezone: string) {
    timezone = timezone || this.getTimezoneCode();
    let timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    return dayjs(date).tz(timezoneFormatter).format(timeConfig.dayAndMonth);
  }

  public getDayAndMonthAndYear(date: string | Date, timezone: string) {
    timezone = timezone || this.getTimezoneCode();
    let timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    const result = dayjs(date).tz(timezoneFormatter).format(timeConfig.pdfTimeFormat);
    return result;
  }

  /**
   * Gets a date key (YYYY-MM-DD) in the specified timezone for grouping purposes
   * This ensures dates are grouped by their local date, not UTC date
   * @param date - The date to format
   * @param timezone - The timezone code (e.g., 'PST', 'EST') or timezoneFormatter
   * @returns Date string in YYYY-MM-DD format in the specified timezone
   */
  public getDateKeyForGrouping(date: string | Date, timezone: string): string {
    if (!date) {
      console.warn('getDateKeyForGrouping: Invalid date provided', date);
      return 'Invalid Date';
    }

    timezone = timezone || this.getTimezoneCode();
    const timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    const formatted = dayjs(date).tz(timezoneFormatter);
    
    // Validate that dayjs parsed the date correctly
    if (!formatted.isValid()) {
      console.warn(`getDateKeyForGrouping: Invalid date provided - ${date}`);
      return 'Invalid Date';
    }
    
    return formatted.format('YYYY-MM-DD');
  }

  public getDayOfWeek(date: Date | string, timezone: string) {
    timezone = timezone || this.getTimezoneCode();
    let timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    const day = dayjs(date).tz(timezoneFormatter).day();
    return day;
  }

  public getDefaultStartTime(timezone: string): string {
    timezone = timezone || this.getTimezoneCode();
    let timezoneFormatter = this.getTimezoneFormatterByTimezone(timezone);
    const dateStr = dayjs().tz(timezoneFormatter).format('YYYY-MM-DD');
    const dateWithTime = dayjs.tz(`${dateStr} 19:00`, 'YYYY-MM-DD HH:mm', timezoneFormatter);
    return dateWithTime.toISOString();
  }

  // DEAD-ish: only consumed by the @deprecated frequency selectors' iterative
  // `for(i=0; i<=diff; i++) addHours(start, 24*i)` generation loops. Once those
  // components are removed, this can go too. Kept for now to avoid breaking callers.
  /** @deprecated Use dayjs(date).add(hours, 'hour') directly, or OccurrenceService for recurrence. */
  public addHours(date: string | Date, hours: number): string {
    return dayjs(date).add(hours, 'hour').toISOString();
  }

  public getTimezoneFormatter() {
    return dayjs.tz.guess();
  }

  // DEAD: only consumed by the @deprecated frequency selectors' adjustForDaylightSaving
  // helper, which is itself dead because rrule.js handles DST internally.
  /** @deprecated rrule.js wall-clock anchoring removes the need for manual DST detection. */
  public getTimezonecodeWithTimeSaving(x: Date | string, formatter: string) {
    let date = dayjs(x);
    formatter = formatter || this.getTimezoneFormatter();
    return date.tz(formatter).format('z');
  }

  public diff(start: string | Date, end: string | Date, type: string = 'days') {
    const a = dayjs(start);
    const b = dayjs(end);

    if (type === 'biweekly') {
      return Math.floor(b.diff(a, 'week') / 2);
    }
    return b.diff(a, type as dayjs.OpUnitType);
  }

  public getTimezoneFormatterByTimezone(timezone: string) {
    if (!timezone) return 'America/New_York';
    if (timezone.includes('/')) return timezone;

    let index = ['EDT', 'EST'].findIndex((x) => x === timezone.toUpperCase());
    if (index !== -1) {
      return 'America/New_York';
    }

    let index1 = ['PDT', 'PST'].findIndex((x) => x === timezone.toUpperCase());
    if (index1 !== -1) {
      return 'America/Los_Angeles';
    }

    let index2 = ['CST', 'CDT'].findIndex((x) => x === timezone.toUpperCase());
    if (index2 !== -1) {
      return 'America/Menominee';
    }

    let index3 = ['MST', 'MDT'].findIndex((x) => x === timezone.toUpperCase());
    if (index3 !== -1) {
      return 'America/Denver';
    }

    let index4 = ['AKST', 'AKDT'].findIndex((x) => x === timezone.toUpperCase());
    if (index4 !== -1) {
      return 'America/Nome';
    }

    let index5 = ['CEST', 'CEDT', 'CET'].findIndex((x) => x === timezone.toUpperCase());
    if (index5 !== -1) {
      return 'Europe/Madrid';
    }

    let index6 = ['HST', 'HDT'].findIndex((x) => x === timezone.toUpperCase());
    if (index6 !== -1) {
      return 'Pacific/Honolulu';
    }
    return 'America/New_York';
  }

  public getTodayRange(timezoneFormatter?: string): { from: Date; to: Date } {
    const timezone = this.resolveTimezoneFormatter(timezoneFormatter);
    const start = dayjs().tz(timezone).startOf('day');
    return {
      from: start.toDate(),
      to: start.endOf('day').toDate(),
    };
  }

  public getTomorrowRange(timezoneFormatter?: string): { from: Date; to: Date } {
    const timezone = this.resolveTimezoneFormatter(timezoneFormatter);
    const start = dayjs().tz(timezone).add(1, 'day').startOf('day');
    return {
      from: start.toDate(),
      to: start.endOf('day').toDate(),
    };
  }

  public getWeekRange(timezoneFormatter?: string): { from: Date; to: Date } {
    const timezone = this.resolveTimezoneFormatter(timezoneFormatter);
    const today = dayjs().tz(timezone);
    const diffToMonday = (today.day() + 6) % 7;
    const start = today.subtract(diffToMonday, 'day').startOf('day');

    return {
      from: start.toDate(),
      to: start.add(6, 'day').endOf('day').toDate(),
    };
  }

  public getWeekendRange() {
    const now = new Date(); // local
    const day = now.getDay(); // 0 = Sun �= Sat

    // Move to *this* Friday
    const daysToFriday = day <= 5 ? 5 - day : -(day - 5);

    /* Friday 00:00 LOCAL */
    const fridayLocal = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + daysToFriday,
      0,
      0,
      0,
      0
    );

    /* Sunday 23:59:59.999 LOCAL */
    const sundayLocal = new Date(fridayLocal);
    sundayLocal.setDate(fridayLocal.getDate() + 2);
    sundayLocal.setHours(23, 59, 59, 999);

    const from = fridayLocal;
    const to = sundayLocal;

    return { from, to };
  }

  public getWeekendRangeInTimezone(timezoneFormatter?: string) {
    const timezone = this.resolveTimezoneFormatter(timezoneFormatter);
    const now = dayjs().tz(timezone);
    const currentDay = now.day();
    const daysToFriday = currentDay <= 5 ? 5 - currentDay : -(currentDay - 5);
    const friday = now.add(daysToFriday, 'day').startOf('day');

    return {
      from: friday.toDate(),
      to: friday.add(2, 'day').endOf('day').toDate(),
    };
  }

  public getMonthRange(timezoneFormatter?: string): { from: Date; to: Date } {
    const timezone = this.resolveTimezoneFormatter(timezoneFormatter);
    const today = dayjs().tz(timezone);
    return {
      from: today.startOf('month').toDate(),
      to: today.endOf('month').toDate(),
    };
  }

  public getSpecificRange(dateRange: IDateRange, timezoneFormatter?: string): { from: Date; to: Date } {
    return {
      from: this.getZonedBoundaryDate(dateRange.start, 'start', timezoneFormatter),
      to: this.getZonedBoundaryDate(dateRange.end, 'end', timezoneFormatter),
    };
  }

  public getSpecificRangeFromDateStrings(
    start: string,
    end: string,
    timezoneFormatter?: string
  ): { from: Date; to: Date } {
    return {
      from: this.getZonedBoundaryDate(start, 'start', timezoneFormatter),
      to: this.getZonedBoundaryDate(end, 'end', timezoneFormatter),
    };
  }

  private getTimezoneCode() {
    const zone = dayjs.tz.guess();
    return dayjs().tz(zone).format('z');
  }

  private resolveTimezoneFormatter(timezoneFormatter?: string) {
    return timezoneFormatter || this.defaultTimezoneFormatter || 'America/New_York';
  }

  private getCurrentTimeInTimezone(timezoneFormatter?: string): Date {
    return dayjs().tz(this.resolveTimezoneFormatter(timezoneFormatter)).toDate();
  }

  private getZonedBoundaryDate(
    input: Date | string,
    boundary: 'start' | 'end',
    timezoneFormatter?: string
  ) {
    const timezone = this.resolveTimezoneFormatter(timezoneFormatter);
    const dateText =
      typeof input === 'string'
        ? input
        : `${input.getFullYear()}-${`${input.getMonth() + 1}`.padStart(2, '0')}-${`${input.getDate()}`.padStart(2, '0')}`;
    const timeText = boundary === 'start' ? '00:00:00.000' : '23:59:59.999';
    return dayjs.tz(`${dateText} ${timeText}`, 'YYYY-MM-DD HH:mm:ss.SSS', timezone).toDate();
  }

  private setTimezoneFormatter(event: VisitorEvent) {
    if (this.defaultTimezoneFormatter) {
      event.timezoneFormatter = this.defaultTimezoneFormatter;
    }

    if (!event.timezoneFormatter) {
      if (!event.timezone) {
        console.log(`Event ${event.name} has no timezone!`);
      }

      event.timezoneFormatter = timezoneConfig.find((x) => x.name.includes(event.timezone))?.value;

      if (!event.timezoneFormatter) console.log(`${event.name}'s timezone formatter not found!`);
    }
  }

  private sortEventTimes(event: VisitorEvent) {
    if (!event.eventTimes || !event.eventTimes.length) {
      event.eventTimes = [{ startTime: event.startTime, endTime: event.endTime }];
    }
    event.eventTimes.sort((a, b) => {
      return a.startTime > b.startTime ? 1 : -1;
    });
  }

  /**
   format for modern-block
   date: Feb 10  - Feb 12, 2023
   time: Fri.3:00 PM - Sun.1:00 PM (EST)
   */
  private getTimeTwoLines(
    time: { startTime: string; endTime: string; isAllDay?: boolean; isEndAllDay?: boolean },
    timezoneFormatter: string
  ): { date: string; time: string } {
    const start = dayjs(time.startTime).tz(timezoneFormatter);
    const end = time.endTime ? dayjs(time.endTime).tz(timezoneFormatter) : null;
    let time1 = { date: '', time: '' };

    // Use explicit isAllDay flag if available, otherwise fall back to time check
    const isStartAllDay = time.isAllDay === true || (start.hour() === 0 && start.minute() === 0 && time.isAllDay !== false);
    // For end time, only treat as "All Day" if explicitly flagged as true
    // If isEndAllDay is false or undefined, show the actual time (even if it's 00:00)
    const isEndAllDay = end ? (time.isEndAllDay === true) : false;
    const isSameDay = end ? start.startOf('day').isSame(end.startOf('day'), 'day') : false;

    if (!end) {
      time1.date = start.format('MMM D, YYYY');
      time1.time = isStartAllDay ? 'All Day' : start.format('ddd, h:mm A (z)');
    } else {
      const startMonthDate = start.format('MMM D');
      const endMonthDate = end.format('MMM D');
      time1.date += startMonthDate;
      if (start.year() !== end.year()) {
        time1.date += `, ${start.year()}`;
      }
      time1.date +=
        startMonthDate === endMonthDate
          ? `, ${end.year()}`
          : ` - ${
              start.month() === end.month() ? end.format('D, YYYY') : end.format('MMM D, YYYY')
            }`;

      // If both are all-day and on the same day, show "All Day"
      if (isStartAllDay && isEndAllDay && isSameDay) {
        time1.time = 'All Day';
      } else {
        // For range dates (different days), handle "All Day" end time
        if (!isSameDay && isEndAllDay) {
          // Range date with "All Day" end: show start time (if not all-day) and "All Day" for end
          if (isStartAllDay) {
            time1.time = 'All Day';
          } else {
            time1.time = `${start.format('ddd, h:mm A')} - All Day`;
          }
        } else if (!isSameDay && isStartAllDay) {
          // Range date with "All Day" start: show "All Day" for start and end time
          const endWeekday = 'ddd, ';
          time1.time = `All Day - ${end.format(`${endWeekday}h:mm A (z)`)}`;
        } else {
          // Same day or both have specific times
          const endWeekday = start.day() === end.day() ? '' : 'ddd, ';
          // hour = 0 and minute = 0 means it's all day event, then hide time
          time1.time =
            (isStartAllDay ? '' : start.format('ddd, h:mm A')) +
            ' - ' +
            (isEndAllDay ? '' : end.format(`${endWeekday}h:mm A (z)`));

          time1.time = time1.time === ' - ' ? '' : time1.time;
          time1.time = time1.time.endsWith(' - ') ? time1.time.replace(' - ', '') : time1.time;
        }
      }
    }

    return time1;
  }

  /**
   format for default, long, square card
   Oct 8th @ 8:00  - Jan 5th @ 9:45 EST
   Jan 19th @ 7:30 EST
   Dec 24 @ 11:00 AM - 9:00 PM EST (for same day events)
   Dec 24 All Day (for all-day events on same day)
   */
  private getTimeOneLine(
    time: { startTime: string; endTime: string; isAllDay?: boolean; isEndAllDay?: boolean },
    timezoneFormatter: string
  ): string {
    const start = dayjs(time.startTime).tz(timezoneFormatter);
    const end = time.endTime ? dayjs(time.endTime).tz(timezoneFormatter) : null;

    // Use explicit isAllDay flag if available, otherwise fall back to time check
    const isStartAllDay = time.isAllDay === true || (start.hour() === 0 && start.minute() === 0 && time.isAllDay !== false);
    // For end time, only treat as "All Day" if explicitly flagged as true
    // If isEndAllDay is false or undefined, show the actual time (even if it's 00:00)
    const isEndAllDay = end ? (time.isEndAllDay === true) : false;

    if (!end) {
      // If no end time and it's all-day, show "All Day"
      if (isStartAllDay) {
        return `${start.format('MMM D')} All Day`;
      }
      return start.format(`MMM D @ h:mm A z`);
    }

    // Check if end is before start (shouldn't happen, but handle gracefully)
    // This can occur due to timezone conversion issues or data errors
    if (end.isBefore(start)) {
      // If end is before start, check if they're actually on the same calendar day
      // by comparing just the date parts (ignoring time)
      const startDateOnly = start.startOf('day');
      const endDateOnly = end.startOf('day');
      
      if (startDateOnly.isSame(endDateOnly, 'day')) {
        // Same calendar day - check if both are all-day
        if (isStartAllDay && isEndAllDay) {
          return `${start.format('MMM D')} All Day`;
        }
        // Same calendar day, just show the time range without end date
        return `${start.format('MMM D @ h:mm A')} - ${end.format('h:mm A z')}`;
      }
      
      // End is truly before start - likely a data error, just show start
      if (isStartAllDay) {
        return `${start.format('MMM D')} All Day`;
      }
      return start.format(`MMM D @ h:mm A z`);
    }

    // Check if start and end are on the same calendar day
    // Use startOf('day') to compare just the date parts, ignoring time
    const isSameDay = start.startOf('day').isSame(end.startOf('day'), 'day');

    // If same day and both are all-day, show: "Dec 24 All day"
    if (isSameDay && isStartAllDay && isEndAllDay) {
      return `${start.format('MMM D')} All day`;
    }

    // If same day, show: "Dec 24 @ 11:00 AM - 9:00 PM EST"
    if (isSameDay) {
      return `${start.format('MMM D @ h:mm A')} - ${end.format('h:mm A z')}`;
    }

    // Different days - show both dates
    // If both are all-day, show "All day" for each
    if (isStartAllDay && isEndAllDay) {
      return `${start.format('MMM D')} All day - ${end.format('MMM D')} All day`;
    }
    if (isStartAllDay) {
      return `${start.format('MMM D')} All day - ${end.format('MMM D @ h:mm A z')}`;
    }
    if (isEndAllDay) {
      return `${start.format('MMM D @ h:mm A')} - ${end.format('MMM D')} All day`;
    }
    return `${start.format('MMM D @ h:mm A')} - ${end.format('MMM D @ h:mm A z')}`;
  }
}

export interface IDateRange {
  start: Date;
  end: Date;
}
